import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  TextField,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Avatar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { getAssociates } from '../../services/associate';
import { leadService } from '../../services/lead';

// Simple debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const LeadTimeLine = () => {
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [associateDirectory, setAssociateDirectory] = useState([]);
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const [associatesError, setAssociatesError] = useState(null);
  const [leadsData, setLeadsData] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const associateSearchTimeoutRef = useRef(null);
  const associatesLoadedRef = useRef(false);

  // Load associates with search functionality
  const loadAssociatesList = useCallback(async (searchTerm = '') => {
    if (loadingAssociates) return;

    // Don't load if already loaded and no search term (prevents unnecessary API calls)
    if (associatesLoadedRef.current && !searchTerm.trim()) return;

    setLoadingAssociates(true);
    setAssociatesError(null);
    try {
      const params = { page: 1, limit: 1000 };
      // Only add filterKey if searchTerm is provided (empty string means show all)
      if (searchTerm && searchTerm.trim()) {
        params.filterKey = searchTerm.trim();
      }
      const response = await getAssociates(params);
      if (response.status && response.data) {
        // Format associates with display label
        const formattedAssociates = response.data.map((associate) => ({
          id: associate._id,
          associateId: associate.associateId,
          name: associate.name || '',
          phone: associate.phone || associate.whatsapp || '',
          displayLabel: `${associate.name || ''} — ${associate.phone || associate.whatsapp || ''}`,
        }));
        setAssociateDirectory(formattedAssociates);
        if (!searchTerm) {
          associatesLoadedRef.current = true;
        }
      } else {
        setAssociatesError('Failed to fetch associates');
      }
    } catch (error) {
      console.error('Error fetching associates:', error);
      setAssociatesError(error.message || 'Failed to fetch associates');
    } finally {
      setLoadingAssociates(false);
    }
  }, [loadingAssociates]);

  // Debounced search effect
  const debouncedAssociateSearch = useMemo(() => {
    return debounce((searchValue) => {
      const trimmedValue = searchValue.trim();
      // Only load associates if:
      // 1. We have a search term (for filtering), OR
      // 2. We haven't loaded associates yet (initial load)
      if (trimmedValue || !associatesLoadedRef.current) {
        loadAssociatesList(trimmedValue);
      }
    }, 500);
  }, [loadAssociatesList]);

  // Initial load
  useEffect(() => {
    if (!associatesLoadedRef.current && !loadingAssociates && associateDirectory.length === 0) {
      loadAssociatesList('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle search input changes
  useEffect(() => {
    if (associateSearchTimeoutRef.current) {
      clearTimeout(associateSearchTimeoutRef.current);
    }

    associateSearchTimeoutRef.current = setTimeout(() => {
      debouncedAssociateSearch(associateSearchInput);
    }, 300);

    return () => {
      if (associateSearchTimeoutRef.current) {
        clearTimeout(associateSearchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associateSearchInput]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (associateSearchTimeoutRef.current) {
        clearTimeout(associateSearchTimeoutRef.current);
      }
    };
  }, []);

  // Handlers for Autocomplete
  const handleAssociateInputChange = useCallback((_, value, reason) => {
    // Update input for user typing and clearing
    // 'reset' happens when option is selected - let onChange handle that
    // 'input' = user typing, 'clear' = cleared
    if (reason === 'input' || reason === 'clear') {
      setAssociateSearchInput(value);
    }
    // Note: We don't update on 'reset' to let onChange handle the selection
  }, []);

  // Fetch leads when associate is selected
  const fetchLeadsByAssociate = useCallback(async (associateId) => {
    if (!associateId) {
      setLeadsData([]);
      return;
    }

    setLoadingLeads(true);
    try {
      const response = await leadService.getLeadsByAssociate(associateId, {
        page: 1,
        limit: 10,
      });

      const leads = Array.isArray(response?.docs) ? response.docs : [];

      // Map simplified API response to component format
      const formattedLeads = leads.map((lead) => ({
        id: lead._id,
        leadId: lead.leadId?.toString() || lead._id,
        name: lead.name || '',
        // Other fields will be empty - full details will be fetched when lead is selected
        phone: '',
        whatsapp: '',
        email: '',
        createdOn: '',
        createdBy: '',
        assignedTo: '',
        address: '',
        projectName: '',
        status: '',
        followUpDetails: [],
        rawData: lead, // Store raw data for reference
      }));

      setLeadsData(formattedLeads);
    } catch (error) {
      console.error('Failed to fetch leads by associate:', error);
      setLeadsData([]);
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  const handleAssociateChange = useCallback((event, value) => {
    console.log('Associate changed:', value);
    setSelectedAssociate(value);
    setSelectedLeadId(''); // Reset lead selection when associate changes
    setSelectedLead(null); // Reset selected lead
    setHasSearched(false); // Reset search state
    
    if (value && value.id) {
      // Associate selected - update input to show selected value
      const displayText = value.displayLabel || `${value.name || ''} — ${value.phone || ''}`;
      setAssociateSearchInput(displayText);
      console.log('Fetching leads for associate ID:', value.id);
      // Associate selected - call getLeadsByAssociate API with associate ID
      fetchLeadsByAssociate(value.id);
    } else {
      // Associate cleared - clear input and reload all associates
      setAssociateSearchInput('');
      setLeadsData([]);
      // Reload all associates with empty filterKey
      associatesLoadedRef.current = false;
      loadAssociatesList('');
    }
  }, [fetchLeadsByAssociate, loadAssociatesList]);

  const handleAssociateOpen = useCallback(() => {
    // Load associates when dropdown opens if not already loaded
    if (!associatesLoadedRef.current && associateDirectory.length === 0) {
      loadAssociatesList('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get available leads (from API)
  const availableLeads = leadsData;

  const handleSearch = async () => {
    if (selectedLeadId) {
      const lead = leadsData.find((l) => l.leadId === selectedLeadId);
      if (lead) {
        // Get the lead's _id (stored in lead.id or lead._id)
        const leadId = lead.id || lead._id;
        
        if (leadId) {
          try {
            // Fetch lead activities
            const activitiesResponse = await leadService.getLeadActivities(leadId, {
              page: 1,
              limit: 20,
            });

            const activities = Array.isArray(activitiesResponse?.docs) ? activitiesResponse.docs : [];

            // Map activities to followUpDetails format
            const formattedActivities = activities.map((activity, index) => ({
              id: activity._id || index + 1,
              date: activity.date || activity.createdAt || '',
              type: activity.type || 'Follow Up',
              remark: activity.remark || '',
              nextFollowUp: activity.nextFollowUpDate || '',
              status: activity.nextFollowUpDate ? 'Scheduled' : 'Completed',
              performedBy: activity.doneBy || activity.createdBy || '',
            }));

            // Update lead with activities and full details from response
            if (activitiesResponse.leadDetails) {
              const details = activitiesResponse.leadDetails;
              setSelectedLead({
                id: details._id,
                leadId: details.leadId?.toString() || lead.leadId,
                name: details.customerInfo?.name || lead.name,
                phone: details.customerInfo?.phone || '',
                whatsapp: details.customerInfo?.whatsapp || '',
                email: details.customerInfo?.email || '',
                profession: details.customerInfo?.profession || '',
                company: details.customerInfo?.company || '',
                createdOn: details.createdAt ? new Date(details.createdAt).toLocaleDateString() : '',
                createdBy: details.createdBy || '',
                assignedTo: details.associateInfo?.name || '',
                associateDesignation: details.associateInfo?.designation || '',
                address: [
                  details.address?.addressLine,
                  details.address?.city,
                  details.address?.state
                ].filter(val => val && val.trim()).join(', ') || 'N/A',
                source: details.source || '',
                projectName: details.source || '', // Using source as project name
                status: details.status || '',
                interestedIn: details.interestedIn || '',
                budget: details.budget || '',
                stage: details.stage || '',
                remarks: details.remarks || {},
                followUpDetails: formattedActivities,
              });
            } else {
              setSelectedLead({
                ...lead,
                followUpDetails: formattedActivities,
              });
            }
          } catch (error) {
            console.error('Failed to fetch lead activities:', error);
            // Use lead data even if activities fetch fails
            setSelectedLead(lead);
          }
        } else {
          setSelectedLead(lead);
        }
      } else {
        setSelectedLead(null);
      }
      setHasSearched(true);
    } else {
      setSelectedLead(null);
      setHasSearched(true);
    }
  };

  const getAvatarInitials = (name) => {
    const nameParts = name.trim().split(' ');
    if (nameParts.length === 1) {
      return name.charAt(0).toUpperCase() + (name.charAt(1) || '').toUpperCase();
    }
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    return firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      '#1976d2',
      '#d32f2f',
      '#388e3c',
      '#f57c00',
      '#7b1fa2',
      '#0097a7',
      '#c2185b',
      '#5d4037',
      '#455a64',
      '#00796b',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Scheduled':
        return 'info';
      case 'Pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Lead Timeline
            </Typography>
            <Typography variant="body2" color="text.secondary">
          Search and track lead activities
            </Typography>
        </Box>

      {/* Search Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#000' }}>
            Lead Detail
          </Typography>
          <Grid container spacing={2} alignItems="center">
            {/* Associate Search Autocomplete */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={associateDirectory}
                getOptionLabel={(option) => option.displayLabel || `${option.name || ''} — ${option.phone || ''}`}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                loading={loadingAssociates}
                inputValue={associateSearchInput}
                onInputChange={handleAssociateInputChange}
                onOpen={handleAssociateOpen}
                value={selectedAssociate}
                onChange={handleAssociateChange}
                clearOnBlur={false}
                selectOnFocus
                handleHomeEndKeys
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Associate Name"
                    placeholder="Search Associate (Name / Phone)"
                    error={!!associatesError}
                    helperText={associatesError}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {loadingAssociates ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <PersonIcon sx={{ fontSize: 18, color: '#536DFE' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.name}
                        </Typography>
                        {option.phone && (
                          <Typography variant="caption" color="text.secondary">
                            {option.phone}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
                noOptionsText={
                  loadingAssociates ? 'Loading associates...' : 'No associates found'
                }
              />
            </Grid>

            {/* Lead Dropdown */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableLeads}
                getOptionLabel={(option) => option ? `${option.leadId} - ${option.name}` : ''}
                isOptionEqualToValue={(option, value) => option.leadId === value?.leadId}
                loading={loadingLeads}
                disabled={!selectedAssociate}
                value={availableLeads.find((lead) => lead.leadId === selectedLeadId) || null}
                onChange={(event, newValue) => {
                  setSelectedLeadId(newValue ? newValue.leadId : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lead"
                    placeholder="Search Lead (Name or ID)"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {loadingLeads ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box component="li" key={key || option.leadId} {...optionProps}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <PersonIcon sx={{ fontSize: 18, color: '#536DFE' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Lead ID: {option.leadId}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                }}
                noOptionsText={
                  loadingLeads ? 'Loading leads...' : 'No leads found'
                }
              />
            </Grid>

            {/* Search Button */}
            <Grid item xs={12} md={4}>
          <Button
                fullWidth
            variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
            sx={{
                  height: 40,
              textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 1,
                }}
              >
                Search
          </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Lead Details Section */}
      {hasSearched && selectedLead && (
        <Box>
          {/* Lead Information Card */}
          <Card sx={{ mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
            <Box
            sx={{
                p: 2,
                backgroundColor: '#536DFE',
                color: 'white',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  sx={{
                    bgcolor: 'white',
                    color: '#536DFE',
                    width: 56,
                    height: 56,
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    border: '3px solid white',
                  }}
                >
                  {getAvatarInitials(selectedLead.name)}
                  </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {selectedLead.name}
                    </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    Lead ID: {selectedLead.leadId}
                    </Typography>
                </Box>
                  <Chip 
                  label={selectedLead.status}
                  sx={{
                    backgroundColor: 'white',
                    color: '#536DFE',
                    fontWeight: 700,
                    fontSize: '14px',
                  }}
                />
              </Box>
              </Box>

            <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <PhoneIcon sx={{ fontSize: 18, color: '#536DFE' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Phone Number
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '15px' }}>
                      {selectedLead.phone}
                    </Typography>
                      </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        WhatsApp Number
                      </Typography>
                      </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '15px' }}>
                      {selectedLead.whatsapp}
                    </Typography>
                      </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <EmailIcon sx={{ fontSize: 18, color: '#536DFE' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Email ID
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '15px' }}>
                      {selectedLead.email}
                      </Typography>
                    </Box>
                  </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <CalendarIcon sx={{ fontSize: 18, color: '#536DFE' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Created On
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '15px' }}>
                      {selectedLead.createdOn}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <PersonIcon sx={{ fontSize: 18, color: '#536DFE' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Created By
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '15px' }}>
                      {selectedLead.createdBy}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <AssignmentIcon sx={{ fontSize: 18, color: '#536DFE' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Assigned To
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '15px' }}>
                      {selectedLead.assignedTo}
                    </Typography>
                    {selectedLead.associateDesignation && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        ({selectedLead.associateDesignation})
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <LocationIcon sx={{ fontSize: 18, color: '#536DFE' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Address
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '15px' }}>
                      {selectedLead.address}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <BusinessIcon sx={{ fontSize: 18, color: '#536DFE' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Project Name
                      </Typography>
                </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '15px' }}>
                      {selectedLead.projectName}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Personal Details Section */}
          <Card sx={{ mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
            <Box
              sx={{
                p: 2,
                backgroundColor: '#536DFE',
                color: 'white',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon sx={{ fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Personal Details
                </Typography>
              </Box>
            </Box>

            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Profession
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '15px' }}>
                      {selectedLead.profession || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Company
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '15px' }}>
                      {selectedLead.company || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Interested In
                    </Typography>
                    <Chip 
                      label={selectedLead.interestedIn || 'N/A'} 
                      size="small" 
                      color="primary" 
                      sx={{ fontWeight: 600 }} 
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Budget
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '15px', color: '#2e7d32' }}>
                      {selectedLead.budget || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Lead Stage
                    </Typography>
                    <Chip 
                      label={selectedLead.stage || 'N/A'} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontWeight: 600 }} 
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Source
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '15px' }}>
                      {selectedLead.source || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>

                {(selectedLead.remarks?.remark1 || selectedLead.remarks?.remark2 || selectedLead.remarks?.generalRemark) && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, backgroundColor: '#fff8e1', borderRadius: 2, border: '1px solid #ffd54f' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Remarks
                      </Typography>
                      {selectedLead.remarks?.remark1 && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Remark 1:</strong> {selectedLead.remarks.remark1}
                        </Typography>
                      )}
                      {selectedLead.remarks?.remark2 && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Remark 2:</strong> {selectedLead.remarks.remark2}
                        </Typography>
                      )}
                      {selectedLead.remarks?.generalRemark && (
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          <strong>General:</strong> {selectedLead.remarks.generalRemark}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
                    
          {/* Follow Up Details Card */}
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                      <Box
                        sx={{
                p: 2,
                backgroundColor: '#536DFE',
                color: 'white',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <TimelineIcon sx={{ fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Follow Up Details
                </Typography>
              </Box>
            </Box>

            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>Date & Time</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>Remark</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>Next Follow Up</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>Performed By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedLead.followUpDetails.map((followUp, index) => (
                      <TableRow key={followUp.id} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>
                            {followUp.date}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={followUp.type}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '12px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '13px' }}>
                            {followUp.remark}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', color: '#1976d2' }}>
                            {followUp.nextFollowUp}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={followUp.status}
                            size="small"
                            color={getStatusColor(followUp.status)}
                            sx={{ fontWeight: 600, fontSize: '12px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{ 
                                width: 28,
                                height: 28,
                                fontSize: '12px',
                                bgcolor: getAvatarColor(followUp.performedBy),
                                fontWeight: 700,
                              }}
                            >
                              {getAvatarInitials(followUp.performedBy)}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>
                              {followUp.performedBy}
                                      </Typography>
                                    </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
                              </CardContent>
                            </Card>
                          </Box>
      )}

      {/* No Results Message */}
      {hasSearched && !selectedLead && (
        <Card sx={{ p: 6, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
          <SearchIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No lead found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please select a lead and click Search to view details
          </Typography>
        </Card>
      )}

      {/* Initial State Message */}
      {!hasSearched && (
        <Card sx={{ p: 6, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
          <SearchIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a lead to view timeline
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose an associate and lead, then click Search to see their activity details
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default LeadTimeLine;
